import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Router } from "@angular/router";
import { CommonModule } from "@angular/common";
import { QuestionnaireStore } from "src/app/features/store/questionnaire-store";
import { QuestionnaireSection } from "src/app/shared/components/questionnaire-section/questionnaire-section";
import { Button } from "src/app/shared/components/utils/button/button";
import { Loader } from "src/app/shared/components/loader/loader";
import { PianoAnalyticsService } from "src/app/core/services/piano-analytics";
import { Modal } from "src/app/shared/components/utils/modal/modal";
import { TranslocoModule } from "@jsverse/transloco";

@Component({
  selector: "app-questionnaire-page",
  standalone: true,
  imports: [
    CommonModule,
    QuestionnaireSection,
    Button,
    Loader,
    Modal,
    TranslocoModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: "./questionnaire-page.html",
})
export class QuestionnairePage implements OnDestroy {
  store = inject(QuestionnaireStore);
  destroyRef = inject(DestroyRef);
  router = inject(Router);
  piano = inject(PianoAnalyticsService);

  isSaving = signal<boolean>(false);

  maturityHasChanged = computed(() => this.store.maturityHasChanged());
  maturity = computed(() => this.store.score()?.maturity);
  isMaturityModalOpen = computed(() => this.store.isModalMaturityOpen());
  modalTitle = computed(() => {
    switch (this.maturity()) {
      case "Low":
        return "questionnairePage.modalLowMaturityTitle";
      case "Medium":
        return "questionnairePage.modalMediumMaturityTitle";
      case "High":
        return "questionnairePage.modalHighMaturityTitle";
      default:
        return "questionnairePage.modalDefaultTitle";
    }
  });
  modalText = computed(() => {
    switch (this.maturity()) {
      case "Low":
        return `questionnairePage.modalLowMaturityText`;
      case "Medium":
        return `questionnairePage.modalMediumMaturityText`;
      case "High":
        return `questionnairePage.modalHighMaturityText`;
      default:
        return `questionnairePage.modalDefaultMaturityText`;
    }
  });
  modalButtonText = computed(() => {
    switch (this.maturity()) {
      case "Low":
        return "questionnairePage.modalLowMaturityButtonText";
      case "Medium":
        return "questionnairePage.modalMediumMaturityButtonText";
      case "High":
        return "questionnairePage.modalHighMaturityButtonText";
      default:
        return "questionnairePage.modalDefaultButtonText";
    }
  });

  @ViewChild("questionnaireContainer")
  questionnaireContainer?: ElementRef<HTMLElement>;

  @HostListener("window:beforeprint")
  handleBeforePrint(): void {
    this.toggleAllDetails(true);
  }

  @HostListener("window:afterprint")
  handleAfterPrint(): void {
    this.toggleAllDetails(false);
  }

  constructor() {
    const handler = (event: BeforeUnloadEvent) => {
      if (!this.store.isDirty()) return;

      event.preventDefault();
    };

    window.addEventListener("beforeunload", handler);

    this.destroyRef.onDestroy(() => {
      window.removeEventListener("beforeunload", handler);
    });

    effect(() => {
      document.body.style.overflow = this.isMaturityModalOpen() ? "hidden" : "";
    });
  }

  ngOnDestroy(): void {
    document.body.style.overflow = "";
  }

  private toggleAllDetails(open: boolean): void {
    const root = this.questionnaireContainer?.nativeElement ?? document.body;
    const details = root.querySelectorAll<HTMLDetailsElement>("details");

    details.forEach((d) => {
      d.open = open;
    });
  }

  async submitQuestionnaire(): Promise<void> {
    try {
      this.isSaving.set(true);
      this.store.setDirty(false);

      const questionnaire = this.store.questionnaire();
      const allSectionAnswers = this.store.sectionAnswers();

      const submissionSections = questionnaire.sections.map(
        (section, sectionIdx) => {
          const sectionAnswers = allSectionAnswers[sectionIdx] || {};

          const submissionQuestions = section.questions.map(
            (q, questionIdx) => {
              return {
                questionId: q.questionId,
                answer: sectionAnswers[questionIdx] || undefined,
              };
            }
          );

          return {
            sectionId: section.sectionId,
            questions: submissionQuestions,
          };
        }
      );

      const submission = {
        sections: submissionSections,
      };

      await this.store.saveSubmission(submission);

      if (this.maturity() === "High") {
        this.piano.sendEvent("questionnaire.submission", {});
      }
    } finally {
      if (this.maturityHasChanged()) {
        this.store.setIsModalMaturityOpen(true);
      } else {
        this.router.navigate(["/user-profile/portal-questionnaire"]);
      }
      this.isSaving.set(false);
    }
  }

  completeModal(): void {
    this.closeModal();
    if (this.maturity() === "High") {
      this.router.navigate(["/user-profile/hr-contact"]);
    } else {
      this.router.navigate(["/user-profile/portal-questionnaire"]);
    }
  }

  closeModal(): void {
    this.store.setIsModalMaturityOpen(false);
  }
}
